import { useState, useEffect } from "react";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { SearchableSelect } from "../components/ui/searchable-select";
import { toast } from "sonner";
import { 
  CalendarDays, 
  Plus, 
  Lock, 
  Unlock, 
  Trash2, 
  Building2,
  Search,
  Users,
  Clock,
  Calendar,
  Edit,
  Save
} from "lucide-react";

const SPECIALTIES = [
  "Physical Therapy",
  "Occupational Therapy",
  "Speech Therapy",
  "Nursing",
  "Radiology",
  "Emergency Medicine",
  "Internal Medicine",
  "Pediatrics",
  "Cardiology",
  "Orthopedics",
];

export default function Rotations() {
  const { user } = useAuth();
  const [rotations, setRotations] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRotation, setEditingRotation] = useState(null);
  const [formData, setFormData] = useState({
    clinical_site_id: "",
    title: "",
    specialty: "",
    start_date: "",
    end_date: "",
    capacity: 1,
    requirements: "",
    description: "",
  });
  const [editFormData, setEditFormData] = useState({
    title: "",
    specialty: "",
    start_date: "",
    end_date: "",
    capacity: 1,
    requirements: "",
  });

  // Format date from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rotationsRes, sitesRes] = await Promise.all([
        api.get("/slots"),
        api.get("/clinical-sites")
      ]);
      setRotations(rotationsRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRotation = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/slots", {
        ...formData,
        capacity: parseInt(formData.capacity),
      });
      setRotations([...rotations, response.data]);
      setDialogOpen(false);
      setFormData({
        clinical_site_id: "",
        title: "",
        specialty: "",
        start_date: "",
        end_date: "",
        capacity: 1,
        requirements: "",
        description: "",
      });
      toast.success("Clinical rotation created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create rotation");
    }
  };

  const handleOpenEditDialog = (rotation) => {
    setEditingRotation(rotation);
    setEditFormData({
      title: rotation.title || "",
      specialty: rotation.specialty || "",
      start_date: rotation.start_date || "",
      end_date: rotation.end_date || "",
      capacity: rotation.capacity || 1,
      requirements: rotation.requirements || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateRotation = async (e) => {
    e.preventDefault();
    if (!editingRotation) return;
    
    try {
      const response = await api.put(`/slots/${editingRotation.id}`, {
        ...editFormData,
        capacity: parseInt(editFormData.capacity),
      });
      setRotations(rotations.map(r => r.id === editingRotation.id ? response.data : r));
      setEditDialogOpen(false);
      setEditingRotation(null);
      toast.success("Rotation updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update rotation");
    }
  };

  const handleToggleLock = async (rotationId) => {
    try {
      const response = await api.put(`/slots/${rotationId}/lock`);
      setRotations(rotations.map(r => r.id === rotationId ? { ...r, is_locked: response.data.is_locked } : r));
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to update rotation lock status");
    }
  };

  const handleDeleteRotation = async (rotationId) => {
    if (!confirm("Are you sure you want to delete this rotation?")) return;
    try {
      await api.delete(`/slots/${rotationId}`);
      setRotations(rotations.filter(r => r.id !== rotationId));
      toast.success("Rotation deleted");
    } catch (error) {
      toast.error("Failed to delete rotation");
    }
  };

  const filteredRotations = rotations.filter((rotation) =>
    (rotation.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    rotation.specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rotation.clinical_site_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDateRange = (start, end) => {
    return `${start} - ${end}`;
  };

  const canManageRotations = user?.role === "admin" || user?.role === "clinical_site";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rotations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Clinical Rotations</h1>
          <p className="text-slate-400 mt-1">
            Manage clinical rotation periods and student slots
          </p>
        </div>
        {canManageRotations && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-cyan-400 text-black hover:bg-cyan-300 font-semibold shadow-[0_0_15px_rgba(34,211,238,0.3)]"
                data-testid="create-rotation-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Rotation
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#151A23] border-slate-700 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-slate-100">Create New Clinical Rotation</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateRotation} className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Clinical Site</Label>
                  <SearchableSelect
                    options={sites.map((site) => ({
                      value: site.id,
                      label: site.name,
                      description: `${site.city}, ${site.state}`,
                    }))}
                    value={formData.clinical_site_id}
                    onValueChange={(value) => setFormData({ ...formData, clinical_site_id: value })}
                    placeholder="Search and select clinical site..."
                    searchPlaceholder="Type to search sites..."
                    emptyText="No matching sites found."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Rotation Title (Optional)</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-slate-800/50 border-slate-700 text-slate-200"
                    placeholder="e.g., Summer PT Clinical Rotation"
                    data-testid="rotation-title-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Specialty</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, specialty: value })}>
                    <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="rotation-specialty-select">
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-slate-700">
                      {SPECIALTIES.map((spec) => (
                        <SelectItem key={spec} value={spec} className="text-slate-200 focus:bg-slate-800">
                          {spec}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Start Date</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      data-testid="rotation-start-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">End Date</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      data-testid="rotation-end-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Available Slots</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-slate-200"
                    data-testid="rotation-capacity-input"
                  />
                  <p className="text-slate-500 text-xs mt-1">Each slot = 1 student for the full rotation period</p>
                </div>
                <div>
                  <Label className="text-slate-300">Requirements (Optional)</Label>
                  <Textarea
                    value={formData.requirements}
                    onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                    className="bg-slate-800/50 border-slate-700 text-slate-200"
                    placeholder="Any prerequisites or requirements..."
                    data-testid="rotation-requirements-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-cyan-400 text-black hover:bg-cyan-300" data-testid="submit-rotation-btn">
                    Create Rotation
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <Input
          placeholder="Search rotations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-slate-800/50 border-slate-700 text-slate-200"
          data-testid="search-rotations-input"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-cyan-400/10 rounded-lg flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Total Rotations</p>
              <p className="text-2xl font-bold text-slate-100">{rotations.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-400/10 rounded-lg flex items-center justify-center">
              <Unlock className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Open Rotations</p>
              <p className="text-2xl font-bold text-slate-100">
                {rotations.filter(r => !r.is_locked && r.capacity > r.filled).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-yellow-400/10 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Locked</p>
              <p className="text-2xl font-bold text-slate-100">
                {rotations.filter(r => r.is_locked).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-400/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Total Slots</p>
              <p className="text-2xl font-bold text-slate-100">
                {rotations.reduce((acc, r) => acc + r.capacity, 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rotations Grid */}
      {filteredRotations.length === 0 ? (
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="py-16 text-center">
            <CalendarDays className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No rotations found</h3>
            <p className="text-slate-500">
              {searchTerm ? "Try a different search term" : "Create your first clinical rotation to get started"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRotations.map((rotation) => (
            <Card 
              key={rotation.id} 
              className={`bg-[#151A23] border-slate-700 hover:border-cyan-400/50 transition-colors ${
                rotation.is_locked ? "opacity-75" : ""
              }`}
              data-testid={`rotation-card-${rotation.id}`}
            >
              <CardHeader className="pb-3">
                {/* Date Range as Primary Identifier */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 bg-cyan-400/10 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-cyan-400 font-mono text-sm font-semibold">
                      {formatDate(rotation.start_date)} - {formatDate(rotation.end_date)}
                    </p>
                    <p className="text-slate-500 text-xs">Rotation Period</p>
                  </div>
                </div>

                {/* Clinical Site */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-slate-200 font-medium flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      {rotation.clinical_site_name}
                    </p>
                    {rotation.title && (
                      <p className="text-slate-500 text-sm mt-1">{rotation.title}</p>
                    )}
                  </div>
                  {rotation.is_locked ? (
                    <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                      <Lock className="w-3 h-3 mr-1" /> Locked
                    </Badge>
                  ) : rotation.capacity <= rotation.filled ? (
                    <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Full</Badge>
                  ) : (
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Open</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <Badge className="bg-cyan-400/10 text-cyan-400 border-cyan-400/20">
                  {rotation.specialty}
                </Badge>
                
                {/* Slots Info */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-300 text-sm">
                      <Users className="w-4 h-4 text-cyan-400" />
                      <span>Slots Filled</span>
                    </div>
                    <span className="text-slate-100 font-semibold">
                      {rotation.filled || 0} / {rotation.capacity}
                    </span>
                  </div>
                  <p className="text-slate-500 text-xs mt-2">{rotation.capacity - (rotation.filled || 0)} slots available</p>
                </div>

                {rotation.requirements && (
                  <p className="text-slate-500 text-sm line-clamp-2">{rotation.requirements}</p>
                )}

                {canManageRotations && (
                  <div className="flex gap-2 pt-2 border-t border-slate-800">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEditDialog(rotation)}
                      className="text-cyan-400 hover:text-cyan-300"
                      data-testid={`edit-rotation-${rotation.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleLock(rotation.id)}
                      className={rotation.is_locked ? "text-yellow-400 hover:text-yellow-300" : "text-slate-400 hover:text-slate-300"}
                      data-testid={`toggle-lock-${rotation.id}`}
                    >
                      {rotation.is_locked ? <Unlock className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                      {rotation.is_locked ? "Unlock" : "Lock"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteRotation(rotation.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-rotation-${rotation.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Rotation Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Edit Rotation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRotation} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Rotation Title (Optional)</Label>
              <Input
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                placeholder="Summer PT Clinical Rotation"
                data-testid="edit-rotation-title-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Specialty</Label>
              <Select value={editFormData.specialty} onValueChange={(value) => setEditFormData({ ...editFormData, specialty: value })}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="edit-rotation-specialty-select">
                  <SelectValue placeholder="Select specialty" />
                </SelectTrigger>
                <SelectContent className="bg-[#151A23] border-slate-700">
                  {SPECIALTIES.map((spec) => (
                    <SelectItem key={spec} value={spec} className="text-slate-200 focus:bg-slate-800">
                      {spec}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Start Date</Label>
                <Input
                  type="date"
                  value={editFormData.start_date}
                  onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                  data-testid="edit-rotation-start-date-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">End Date</Label>
                <Input
                  type="date"
                  value={editFormData.end_date}
                  onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                  data-testid="edit-rotation-end-date-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Available Slots</Label>
              <Input
                type="number"
                min="1"
                value={editFormData.capacity}
                onChange={(e) => setEditFormData({ ...editFormData, capacity: e.target.value })}
                required
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                data-testid="edit-rotation-capacity-input"
              />
              <p className="text-slate-500 text-xs mt-1">Each slot = 1 student for the full rotation period</p>
              {editingRotation && editingRotation.filled > 0 && (
                <p className="text-yellow-400 text-xs mt-1">
                  Note: {editingRotation.filled} slot(s) already filled
                </p>
              )}
            </div>
            <div>
              <Label className="text-slate-300">Requirements (Optional)</Label>
              <Textarea
                value={editFormData.requirements}
                onChange={(e) => setEditFormData({ ...editFormData, requirements: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                placeholder="Any prerequisites or requirements..."
                data-testid="edit-rotation-requirements-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-400 text-black hover:bg-cyan-300" data-testid="save-rotation-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
